/** Helpers both the hover and the click handlers need. */

import { CFG, MM, nodeY, type PlankWidth } from "../../data/haki.ts";
import type { StairSpan } from "../../model/geometry.ts";
import { bayEdges, findBoardSpans, legAt, near } from "../../model/geometry.ts";
import { boardBlocked } from "../../model/operations.ts";
import { autoSpir } from "../../model/solver.ts";
import type { Board, Leg, Point, ScaffoldModel } from "../../model/types.ts";
import type { ToolContext } from "./toolContext.ts";
import type { BoardPlacement, UtvPlacement } from "./types.ts";

/** Boards pack from this far inside the span's overlap, metres — the same margin a deck starts at. */
const BOARD_MARGIN = 0.045;

/** Perpendicular distance from `point` to the segment a–b, or infinity beyond its ends. */
function segmentDistance(a: Point, b: Point, point: Point): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz) || 1;
  const t = ((point.x - a.x) * dx + (point.z - a.z) * dz) / (len * len);
  if (t < -0.02 || t > 1.02) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.abs((point.x - a.x) * (-dz / len) + (point.z - a.z) * (dx / len));
}

/** Is the point between the span's two edges and inside their overlap? */
function spanContains(span: StairSpan, point: Point): boolean {
  const across = (point.x - span.lo.a.x) * span.u.x + (point.z - span.lo.a.z) * span.u.z;
  const along = (point.x - span.lo.a.x) * span.dir.x + (point.z - span.lo.a.z) * span.dir.z;

  return (
    across >= span.o0 - 0.05 && across <= span.o1 + 0.05 && along >= -0.05 && along <= span.len * MM + 0.05
  );
}

/**
 * Which way a board goes where several spans overlap — inside a bay, both
 * pairs of edges qualify. As with a whole-bay deck, the board rests on the
 * edge nearest the pointer and spans to the one opposite.
 */
export function chooseBoardSpan(
  model: ScaffoldModel,
  wid: PlankWidth,
  p: number,
  point: Point,
): StairSpan | null {
  const spans = findBoardSpans(model, wid).filter((span) => span.p === p && spanContains(span, point));
  const first = spans[0];
  if (!first) {
    return null;
  }

  let nearest: { dx: number; dz: number; distance: number } | null = null;
  for (const edge of bayEdges(model)) {
    if (edge.p !== p) {
      continue;
    }

    const distance = segmentDistance(edge.a, edge.b, point);
    if (!nearest || distance < nearest.distance) {
      const len = Math.hypot(edge.b.x - edge.a.x, edge.b.z - edge.a.z) || 1;
      nearest = { dx: (edge.b.x - edge.a.x) / len, dz: (edge.b.z - edge.a.z) / len, distance };
    }
  }
  if (!nearest) {
    return first;
  }

  const rest = nearest;
  return spans.find((span) => Math.abs(span.u.x * rest.dx + span.u.z * rest.dz) > 0.98) ?? first;
}

/**
 * Which slot across the span the pointer is in, and the board that fills it.
 * Slots are one board wide from the overlap's start, so hand-laid boards pack
 * edge to edge; the board runs from the first edge's axis to the second's.
 */
export function planBoard(
  model: ScaffoldModel,
  span: StairSpan,
  wid: PlankWidth,
  point: Point,
): BoardPlacement {
  const width = wid * MM;
  const raw = (point.x - span.lo.a.x) * span.u.x + (point.z - span.lo.a.z) * span.u.z;
  const first = span.o0 + BOARD_MARGIN + width / 2;
  const last = span.o1 - BOARD_MARGIN - width / 2;
  const t = Math.max(first, Math.min(last, first + Math.round((raw - first) / width) * width));
  const gap = Math.abs((span.hi.a.x - span.lo.a.x) * span.dir.x + (span.hi.a.z - span.lo.a.z) * span.dir.z);

  const a = { x: +(span.lo.a.x + span.u.x * t).toFixed(3), z: +(span.lo.a.z + span.u.z * t).toFixed(3) };
  const b = { x: +(a.x + span.dir.x * gap).toFixed(3), z: +(a.z + span.dir.z * gap).toFixed(3) };
  const board: Board = { a, b, p: span.p, wid };

  return { board, blocked: boardBlocked(model, board) };
}

/**
 * The standard a click with the Standard tool would stack onto: the one under
 * the pointer, or the one standing on the bracket under the pointer — a bare
 * bracket stub is a standard with no segments yet.
 */
export function stackTarget(ctx: ToolContext, event: PointerEvent): Leg | null {
  const legHit = ctx.pickElem(event, (ref) => ref.type === "leg");
  if (legHit?.ref.type === "leg") {
    return ctx.model.legs.get(legHit.ref.key) ?? null;
  }

  const bracketHit = ctx.pickElem(event, (ref) => ref.type === "bracket");
  if (bracketHit?.ref.type === "bracket") {
    const bracket = ctx.model.brackets[bracketHit.ref.idx];
    return bracket ? (legAt(ctx.model, bracket.b.x, bracket.b.z) ?? null) : null;
  }

  return null;
}

export type FreeEndPlan = Readonly<{ segs: number[]; gy: number; reaches: boolean }>;

/**
 * Which standard to drop at a free beam end: the same length as the segment the
 * beam started from when it reaches, otherwise the shortest single that does.
 */
export function planFreeEnd(ctx: ToolContext, x: number, z: number, p: number): FreeEndPlan {
  const gy = ctx.terrain.resolveGy(x, z, nodeY(p));
  const at: Point = { x, z };
  const beam = ctx.model.beams.find((b) => b.p === p && (near(b.a, at) || near(b.b, at)));
  const other = beam ? (near(beam.a, at) ? beam.b : beam.a) : null;
  const startLeg = other ? (legAt(ctx.model, other.x, other.z) ?? null) : null;
  const plan = autoSpir(p, gy, startLeg, (px, pz) => ctx.terrain.groundY(px, pz));

  return { segs: plan.segs, gy, reaches: plan.reaches };
}

/** Which end of a transom pair the pointer is nearest, and where the flight sits. */
export function resolveUtvPlacement(spans: StairSpan[], point: Point): UtvPlacement | null {
  let chosen: StairSpan | null = null;
  let best = Number.POSITIVE_INFINITY;

  for (const span of spans) {
    const along = (point.x - span.lo.a.x) * span.dir.x + (point.z - span.lo.a.z) * span.dir.z;
    if (Math.abs(along) < best) {
      best = Math.abs(along);
      chosen = span;
    }
  }
  if (!chosen) {
    return null;
  }

  const halfWidth = (CFG.utv.width * MM) / 2;
  const raw = (point.x - chosen.lo.a.x) * chosen.u.x + (point.z - chosen.lo.a.z) * chosen.u.z;
  const t = Math.max(chosen.o0 + halfWidth, Math.min(chosen.o1 - halfWidth, raw));

  return {
    o: { x: chosen.lo.a.x + chosen.u.x * t, z: chosen.lo.a.z + chosen.u.z * t },
    dx: chosen.dir.x,
    dz: chosen.dir.z,
    len: chosen.len,
    p: chosen.p,
  };
}

/** Which side of a beam edge the pointer is on — the direction a small stair descends. */
export function outwardFromEdge(a: Point, b: Point, point: Point): { dx: number; dz: number } {
  const ex = b.x - a.x;
  const ez = b.z - a.z;
  const length = Math.hypot(ex, ez);
  const px = -ez / length;
  const pz = ex / length;
  const mx = (a.x + b.x) / 2;
  const mz = (a.z + b.z) / 2;
  const sign = (point.x - mx) * px + (point.z - mz) * pz >= 0 ? 1 : -1;

  return { dx: px * sign, dz: pz * sign };
}

export function formatLevel(p: number): string {
  return (p * 0.5).toLocaleString("en", { minimumFractionDigits: 1 });
}
